import React, { useState } from "react";
import Marquee from "../components/brand/Marquee";
import Nav from "../components/brand/Nav";
import Footer from "../components/brand/Footer";
import FrdmBucksCard from "../components/brand/FrdmBucksCard";
import StickerBadge from "../components/brand/StickerBadge";
import { useScrollReveal } from "../lib/useScrollReveal";
import { toast } from "sonner";
import { createEmailSignup } from "../services/api";
import { ArrowRight } from "lucide-react";

const steps = [
  {
    n: "01",
    title: "DROP YOUR EMAIL",
    body: "One field. No app. No password. Welcome.",
  },
  {
    n: "02",
    title: "WALK INTO A CAFÉ",
    body: "Polk Street to start. More zip codes coming.",
  },
  {
    n: "03",
    title: "ORDER LIKE A REGULAR",
    body: "Free refills. Member-only perks. Random good days.",
  },
];

const benefits = [
  "Free refills, all day",
  "Member-only roasts",
  "Early access to new cafés",
  "Birthday brew, on us",
  "First dibs on merch drops",
  "Random surprise pours",
];

const FrdmBucks = () => {
  const pageRef = useScrollReveal();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createEmailSignup(email);
      toast.success("Welcome to the club. Your card is in the mail (kind of).");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 400) {
        toast.success("You're already a member. See you at the counter.");
        setEmail("");
      } else {
        toast.error("Something went sideways. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={pageRef}
      data-testid="page-frdmbucks"
      className="bg-bone text-navy"
    >
      <Marquee />
      <Nav />

      {/* HERO */}
      <section className="bg-red text-bone relative grain pt-20 md:pt-28 pb-28 md:pb-40 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-7 relative">
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-bone/80 mb-4">
              ◢◢ MEMBERS ONLY · EST. 2026
            </p>
            <h1
              data-testid="frdmbucks-headline"
              className="font-display uppercase leading-[0.82] tracking-tighter text-[16vw] md:text-[12vw] lg:text-[10vw]"
            >
              FRDM<br />BUCKS.
            </h1>
            <p className="font-body text-lg md:text-xl leading-relaxed mt-6 max-w-md text-bone/90">
              The loyalty program for people who drink coffee like they mean it.
              Free refills. Better days. No app required.
            </p>
          </div>

          <div className="col-span-12 md:col-span-5 relative flex justify-center items-center">
            <FrdmBucksCard tilt={-8} />
            <div className="absolute -top-2 -left-1 md:top-4 md:-left-4">
              <StickerBadge color="gold" textColor="navy" rotate={-12} size={130}>
                FREE<br />REFILLS
              </StickerBadge>
            </div>
            <div className="absolute -bottom-2 right-0 md:-bottom-6 md:right-12">
              <StickerBadge color="navy" textColor="bone" rotate={14} size={120}>
                MEMBERS<br />ONLY
              </StickerBadge>
            </div>
            <div className="hidden md:block absolute bottom-32 -right-4">
              <StickerBadge color="magenta" textColor="bone" rotate={-8} size={100}>
                EST.<br />2026
              </StickerBadge>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-bone text-navy py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
          <div className="flex justify-between items-end mb-12 md:mb-16">
            <h2 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
              How it <span className="text-red">works.</span>
            </h2>
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-navy/60">
              § GUIDE
            </p>
          </div>

          <div className="grid grid-cols-12 gap-8 md:gap-10">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className="col-span-12 md:col-span-4 relative reveal"
                data-testid={`step-${s.n}`}
              >
                <p className="font-display text-red leading-none text-[28vw] md:text-[12vw] -mb-4 md:-mb-6">
                  {s.n}
                </p>
                <h3 className="font-display uppercase leading-none tracking-tight text-3xl md:text-4xl">
                  {s.title}
                </h3>
                <p className="font-body text-base md:text-lg leading-relaxed mt-3 max-w-xs">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-navy text-bone py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red mb-4">
              § PERKS
            </p>
            <h2 className="font-display uppercase leading-[0.85] tracking-tight text-5xl md:text-7xl">
              The <span className="text-red">spoils.</span>
            </h2>
          </div>

          <ul className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {benefits.map((b, i) => (
              <li
                key={i}
                data-testid={`benefit-${i}`}
                className="border-b-2 border-bone/20 py-5 flex items-center gap-4 group"
              >
                <span className="font-mono uppercase tracking-[0.3em] text-[10px] text-red w-10">
                  0{i + 1}
                </span>
                <span className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-none group-hover:text-red transition-colors">
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SIGNUP FORM */}
      <section
        data-testid="frdmbucks-signup"
        className="bg-red text-bone py-24 md:py-32 grain"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-12 text-center">
          <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-6xl md:text-8xl">
            BECOME A <span className="text-navy">MEMBER.</span>
          </h2>
          <p className="font-mono uppercase tracking-[0.25em] text-xs md:text-sm text-bone/90 mt-6">
            ◢ ONE EMAIL · ZERO FRICTION
          </p>

          <form
            onSubmit={handleJoin}
            className="mt-10 flex flex-col md:flex-row gap-4 max-w-2xl mx-auto"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="frdmbucks-email-input"
              className="flex-1 bg-transparent border-4 border-bone rounded-none px-5 py-4 font-mono text-base md:text-lg text-bone placeholder:text-bone/50 brut-focus"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="frdmbucks-join-button"
              className="bg-navy text-bone hover:bg-bone hover:text-navy transition-colors font-display uppercase tracking-wider text-xl md:text-2xl px-8 py-4 border-4 border-bone disabled:opacity-60 inline-flex items-center gap-3 justify-center"
            >
              {loading ? "Joining…" : "Become a Member"} <ArrowRight size={22} />
            </button>
          </form>

          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-bone/70 mt-8 max-w-2xl mx-auto leading-relaxed">
            ◢ Fine print: Membership is free. We won't sell your email. Stored
            value features (gift cards, reloads) launch with our mobile rollout.
            Full terms available on request.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FrdmBucks;
